// Mirrors customised-nudge writes to the MongoDB nudges API.
//
// This is ADDITIVE: it runs alongside the existing Firebase writes and never
// throws — if the API is unreachable or errors, we only log a warning so the
// Firebase flow (and the UI) keep working exactly as before.
//
// Backend (same base URL as the rest of the dashboard):
//   PUT    /api/nudges  -> upsert a nudge   { collectionName, nudgeName, nudge, personaType? }
//   DELETE /api/nudges  -> delete a nudge   { collectionName, nudgeName }
// collectionName === client name (the same `user` value used in the Firebase path).

const NUDGES_API_URL = `${process.env.REACT_APP_API_BASE_URL}/api/nudges`;

/**
 * Upsert (create or update) a single nudge in MongoDB.
 * @param {string} user        client name (collectionName)
 * @param {string} nudgeName   nudge key (same as the Firebase userNudgeData key)
 * @param {object} nudge       full nudge config object
 * @param {object} [personaType] optional { optionName, optionValue }; omitted -> existing value preserved
 */
export const upsertNudgeToMongo = async (user, nudgeName, nudge, personaType) => {
  try {
    if (!user || !nudgeName) return false;

    const body = { collectionName: user, nudgeName, nudge };
    if (personaType !== undefined) body.personaType = personaType;

    const res = await fetch(NUDGES_API_URL, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });

    if (!res.ok) {
      console.warn(`[nudgeMongoApi] upsert failed (${res.status}) for "${nudgeName}"`);
      return false;
    }
    return true;
  } catch (error) {
    console.warn("[nudgeMongoApi] upsert error:", error?.message || error);
    return false;
  }
};

/**
 * One-time backfill: push the currently-loaded Firebase nudges into MongoDB.
 * Idempotent (each nudge is a PUT upsert), so it is safe to run repeatedly.
 * @param {string} user             client name (collectionName)
 * @param {object} usersNudgeData   { [nudgeName]: nudgeConfig }
 * @param {Array}  [userPersonaTypes] [{ optionName, optionValue }] — matched by optionValue === nudgeName
 * @returns {Promise<{total:number, success:number, failed:number}>}
 */
export const backfillNudgesToMongo = async (user, usersNudgeData, userPersonaTypes = []) => {
  const summary = { total: 0, success: 0, failed: 0 };
  if (!user || !usersNudgeData || typeof usersNudgeData !== "object") return summary;

  const personaByValue = Array.isArray(userPersonaTypes)
    ? userPersonaTypes.reduce((acc, p) => {
        if (p && p.optionValue) acc[p.optionValue] = p;
        return acc;
      }, {})
    : {};

  const entries = Object.entries(usersNudgeData).filter(([, nudge]) => nudge != null);
  summary.total = entries.length;

  const results = await Promise.allSettled(
    entries.map(([nudgeName, nudge]) =>
      upsertNudgeToMongo(user, nudgeName, nudge, personaByValue[nudgeName])
    )
  );

  results.forEach((r) => {
    if (r.status === "fulfilled" && r.value) summary.success += 1;
    else summary.failed += 1;
  });

  return summary;
};

/**
 * Delete a single nudge from MongoDB.
 * @param {string} user       client name (collectionName)
 * @param {string} nudgeName  nudge key to delete
 */
export const deleteNudgeFromMongo = async (user, nudgeName) => {
  try {
    if (!user || !nudgeName) return;

    const res = await fetch(NUDGES_API_URL, {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ collectionName: user, nudgeName }),
    });

    if (!res.ok && res.status !== 404) {
      console.warn(`[nudgeMongoApi] delete failed (${res.status}) for "${nudgeName}"`);
    }
  } catch (error) {
    console.warn("[nudgeMongoApi] delete error:", error?.message || error);
  }
};
